'use client';
import { analiticaStatusPageEvents } from '@payments-checkout/shared/plugins/analitica/statusPage.events';
import { ProgressIndicator, Skeleton, Text } from '@stone-payments/jade';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useExpirationProgress } from '../../hooks/useExpirationProgress';
import { CopyButton } from '../copyButton';
import { isExpired } from '../trackingStatus/expirationHelper';
import { ExpiredPixIcon } from './expiredPixIcon';

interface PixTrackingStatusProps {
  qrCodeImg?: string;
  qrCodeText?: string;
  expirationDate?: string;
}

export function PixTrackingStatus({
  qrCodeImg,
  qrCodeText,
  expirationDate,
}: PixTrackingStatusProps = {}) {
  const { formatMessage } = useIntl();
  const { progressValue, timeRemaining } =
    useExpirationProgress(expirationDate);
  const expired = isExpired(expirationDate);
  const [isQrCodeLoaded, setIsQrCodeLoaded] = useState(false);

  return (
    <div className="w-full space-y-jade-300 mt-jade-200">
      <div className="text-center px-jade-200 py-jade-300">
        <div className="text-center mb-jade-150">
          <Text
            color="current-color"
            variant="heading-medium"
            className="text-center"
          >
            {expired
              ? formatMessage({ id: 'pix.heading.expired' })
              : formatMessage({ id: 'pix.heading' })}
          </Text>
        </div>

        <Text variant="text-large" color="medium" className="text-center">
          {expired
            ? formatMessage({ id: 'pix.instruction.expired' })
            : formatMessage({ id: 'pix.instruction' })}
        </Text>

        {expired ? (
          <div className="mb-jade-50 mt-jade-400 flex flex-col items-center gap-jade-50">
            <ExpiredPixIcon />
          </div>
        ) : (
          <div className="mb-jade-50 mt-jade-400 flex flex-col items-center gap-jade-50">
            {!isQrCodeLoaded && <Skeleton width={200} height={200} />}
            <div className="bg-jade-background-surface p-jade-50 border-jade-xsmall border-jade-border-low border-solid rounded-jade-small">
              <img
                src={qrCodeImg}
                alt="QR Code Pix"
                onLoad={() => setIsQrCodeLoaded(true)}
                onError={() => setIsQrCodeLoaded(true)}
                className={`w-[200px] h-[200px] ${
                  isQrCodeLoaded ? '' : 'hidden'
                }`}
              />
            </div>

            <Text
              color="high"
              variant="text-small"
              weight="semibold"
              className="text-center break-all"
            >
              {qrCodeText}
            </Text>
            <div className="flex flex-col items-center gap-jade-250 mt-jade-150">
              <CopyButton
                textToCopy={qrCodeText || ''}
                onCopy={() =>
                  analiticaStatusPageEvents.events.statusPage.copyPixCodeClicked()
                }
              >
                {formatMessage({ id: 'statusPage.copyButton' })}
              </CopyButton>
              <ProgressIndicator
                value={progressValue}
                hideValue
                hideLabel
                size="large"
                variant="bar"
                supportText={`${formatMessage({
                  id: 'pix.waitingPayment',
                })} ${timeRemaining}`}
                label={`${formatMessage({
                  id: 'pix.waitingPayment',
                })} ${timeRemaining}`}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
